import { router } from "@inertiajs/react"
import { Search, X } from "lucide-react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"

import type {
  FilterOption,
  WorkOrderFilterOptions,
  WorkOrderFilters,
} from "./types"

/**
 * FILTERING IS SERVER-SIDE. A fleet is thousands of cars against years of
 * history; shipping the table to the browser and filtering there works for a
 * demo and falls over in month two. Every control here is a URL change.
 *
 * Radix Select cannot hold an empty-string value, so "any" is the sentinel for
 * "no filter" and is stripped before the request goes out.
 */
const ANY = "any"

export function WorkOrderFilterBar({
  filters,
  options,
  path,
  counts,
}: {
  filters: WorkOrderFilters
  options: WorkOrderFilterOptions
  /** Index or board — the same filters drive both views. */
  path: string
  counts: { open: number; closed: number; pending_review: number }
}) {
  const [query, setQuery] = useState(filters.q ?? "")

  function apply(patch: Partial<Record<string, string | null>>) {
    const next: Record<string, string> = {}
    const merged = { ...serialize(filters), ...patch }
    for (const [key, value] of Object.entries(merged)) {
      if (value && value !== ANY) next[key] = value
    }
    router.get(path, next, { preserveState: true, preserveScroll: true })
  }

  const active = activeCount(filters)

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <ToggleGroup
          type="single"
          value={filters.state}
          onValueChange={(value) => value && apply({ state: value })}
          variant="outline"
          size="sm"
        >
          <ToggleGroupItem value="open">Open ({counts.open})</ToggleGroupItem>
          <ToggleGroupItem value="closed">
            Closed ({counts.closed})
          </ToggleGroupItem>
          <ToggleGroupItem value="all">All</ToggleGroupItem>
        </ToggleGroup>

        <form
          className="flex items-center gap-1"
          onSubmit={(event) => {
            event.preventDefault()
            apply({ q: query || null })
          }}
        >
          <div className="relative">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 size-4 -translate-y-1/2" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Car number"
              aria-label="Search by car number"
              className="h-9 w-40 pl-8 uppercase"
            />
          </div>
          <Button type="submit" size="sm" variant="secondary">
            Find
          </Button>
        </form>

        <FilterSelect
          label="Type"
          value={filters.type}
          options={options.types}
          onChange={(value) => apply({ type: value })}
        />
        <FilterSelect
          label="Status"
          value={filters.status}
          options={options.statuses}
          onChange={(value) => apply({ status: value })}
        />
        <FilterSelect
          label="Waiting on"
          value={filters.waiting_on}
          options={options.waiting_on}
          onChange={(value) => apply({ waiting_on: value })}
        />
        <FilterSelect
          label="Facility"
          value={filters.facility_id}
          options={options.facilities}
          onChange={(value) => apply({ facility_id: value })}
        />
        <FilterSelect
          label="Fleet"
          value={filters.fleet_code}
          options={options.fleet_codes}
          onChange={(value) => apply({ fleet_code: value })}
        />
        <FilterSelect
          label="Lessor"
          value={filters.lessor_id}
          options={options.lessors}
          onChange={(value) => apply({ lessor_id: value })}
        />
        <FilterSelect
          label="Shop"
          value={filters.shop_id}
          options={options.shops}
          onChange={(value) => apply({ shop_id: value })}
        />
        {options.specialists.length > 0 && (
          <FilterSelect
            label="Specialist"
            value={filters.specialist}
            options={options.specialists}
            onChange={(value) => apply({ specialist: value })}
          />
        )}

        {active > 0 && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setQuery("")
              router.get(
                path,
                { state: filters.state },
                { preserveScroll: true },
              )
            }}
          >
            <X className="mr-1 size-4" />
            Clear {active}
          </Button>
        )}
      </div>
    </div>
  )
}

function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string | null
  options: FilterOption[]
  onChange: (value: string | null) => void
}) {
  if (options.length === 0) return null

  return (
    <Select
      value={value ?? ANY}
      onValueChange={(next) => onChange(next === ANY ? null : next)}
    >
      <SelectTrigger size="sm" className="w-auto min-w-32" aria-label={label}>
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ANY}>{label}: any</SelectItem>
        {options.map((option) => (
          <SelectItem key={option.value} value={String(option.value)}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function serialize(filters: WorkOrderFilters): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [key, value] of Object.entries(filters)) {
    if (value != null && value !== "") out[key] = String(value)
  }
  return out
}

/** `state` and `sort` always have a value, so they aren't "active filters". */
function activeCount(filters: WorkOrderFilters): number {
  return Object.entries(filters).filter(
    ([key, value]) =>
      value != null && value !== "" && key !== "state" && key !== "sort",
  ).length
}
